import { TranslationKey } from '@Localization';

export enum CUSTOM_ERROR_CODES {
  WRONG_IMAGE_SIZE = 'WRONG_IMAGE_SIZE',
  ANNOTATIONS_MISMATCH = 'ANNOTATIONS_MISMATCH',
  COST_LIMIT_EXCEEDED = 'COST_LIMIT_EXCEEDED',
  EMPTY_TRAIN_DATASET = 'EMPTY_TRAIN_DATASET',
  NO_TRAIN_DATASETS = 'NO_TRAIN_DATASETS',
  MISSING_FILES = 'MISSING_FILES',
  TRAINING_NOT_FOUND = 'TRAINING_NOT_FOUND',
  EVALUATION_NOT_FOUND = 'EVALUATION_NOT_FOUND',
  MISSING_ANNOTATIONS = 'MISSING_ANNOTATIONS',
  MISSING_ANNOTATIONS_TRAIN = 'MISSING_ANNOTATIONS_TRAIN',
  EMPTY_VALID_DATASET = 'EMPTY_VALID_DATASET',
  TOO_MANY_QUESTIONS = 'TOO_MANY_QUESTIONS',
  TRAIN_ANNOTATIONS_MISMATCH = 'TRAIN_ANNOTATIONS_MISMATCH',
  VALIDATION_ANNOTATIONS_MISMATCH = 'VALIDATION_ANNOTATIONS_MISMATCH',
  QUESTION_ANNOTATION_MISMATCH = 'QUESTION_ANNOTATION_MISMATCH',
  GOOD_IMAGE_WITH_DEFECTS = 'GOOD_IMAGE_WITH_DEFECTS',
  TRAIN_VALIDATION_ANNOTATIONS_MISMATCH = 'TRAIN_VALIDATION_ANNOTATIONS_MISMATCH',
  INVALID_ANNOTATION_TOOL_NAME = 'INVALID_ANNOTATION_TOOL_NAME',
  WORKSPACE_SPACE_LIMIT_REACHED = 'WORKSPACE_SPACE_LIMIT_REACHED',
}

export class ACDLError extends Error {
  apiErrorResponse?: ApiErrorResponse;

  constructor(m: string, apiErrorResponse?: ApiErrorResponse, cause?: ErrorOptions) {
    super(m, cause);
    this.apiErrorResponse = apiErrorResponse;

    // Set the prototype explicitly.
    Object.setPrototypeOf(this, ACDLError.prototype);
  }

  isBadRequestWithCode(codes: string[]): boolean {
    return this.isBadRequest() && this.withCode(codes);
  }

  isBadRequest(): boolean {
    return this.apiErrorResponse?.statusCode === 400;
  }

  withCode(codes: string[]): boolean {
    return this.apiErrorResponse?.errorCodes?.some((r) => codes.includes(r)) || false;
  }

  hasErrorCodes(): boolean {
    return (this.apiErrorResponse?.errorCodes?.length ?? 0) > 0;
  }
}

export class UnauthorizedError extends ACDLError {
  constructor() {
    super('Unauthorized', new ApiErrorResponse(401, 'Unauthorized', []));
    Object.setPrototypeOf(this, UnauthorizedError.prototype);
  }
}

export class ForbiddenError extends ACDLError {
  constructor() {
    super('Forbidden', new ApiErrorResponse(403, 'Forbidden', []));
    Object.setPrototypeOf(this, ForbiddenError.prototype);
  }
}

export class CancelledError extends ACDLError {
  constructor() {
    super('Request Cancelled');
    Object.setPrototypeOf(this, CancelledError.prototype);
  }
}

export class LockedUserError extends ACDLError {
  constructor() {
    super('Locked', new ApiErrorResponse(460, 'Locked', []));
    Object.setPrototypeOf(this, LockedUserError.prototype);
  }
}

export class PreprocessingError extends ACDLError {
  constructor(error: Error) {
    super('Preprocessing Error', undefined, { cause: error });
    Object.setPrototypeOf(this, PreprocessingError.prototype);
  }
}

export class VersionMissmatchError extends ACDLError {
  constructor() {
    super('Version Missmatch Error');
    Object.setPrototypeOf(this, VersionMissmatchError.prototype);
  }
}

export class ApiErrorResponse {
  constructor(
    public readonly statusCode: number,
    public readonly message: string,
    public readonly errorCodes: TranslationKey[]
  ) {}

  public readonly errorContext: object = {};
}

export class InputStatus {
  constructor(
    public isValid?: boolean | undefined,
    public errorCodes: TranslationKey[] = []
  ) {}

  static empty(): InputStatus {
    return new InputStatus(undefined, []);
  }

  static valid(): InputStatus {
    return new InputStatus(true, []);
  }

  static invalid(): InputStatus {
    return new InputStatus(false, []);
  }

  static buildFrom(errorCodes: TranslationKey[]): InputStatus {
    return new InputStatus(errorCodes.length === 0, errorCodes);
  }

  addErrorCode(code: TranslationKey): void {
    this.errorCodes.push(code);
    this.isValid = false;
  }

  get isEmpty(): boolean {
    return this.isValid === undefined;
  }
}

export class OperationResult {
  public isSuccess: boolean = true;
  public Error?: ACDLError = undefined;

  constructor(isSuccess: boolean, error?: ACDLError) {
    this.isSuccess = isSuccess;
    this.Error = error;
  }
}

export class Failed extends OperationResult {
  constructor(error?: ACDLError) {
    super(false, error);
    this.isHandled = false;
  }

  public isHandled: boolean;
}

export class UploadSuccess extends OperationResult {
  public imageSize: number;

  constructor(imageSize: number) {
    super(true);
    this.imageSize = imageSize;
  }
}
